import * as THREE from 'three';
import { DemoRoom } from './demo-room.js';
import type { Stage } from './stage.js';

/**
 * AR measuring with WebXR hit-test.
 *
 * One hit-test source per input source, so a controller ray and a hand ray
 * both work and the reticle follows whichever is pointing. On Quest 3/3S the
 * Depth API backs this, so it works without a room scan up to about 5 m
 * (docs/09-research.md).
 *
 * Outside an XR session it falls back to a virtual floor and wall, so the flow
 * can be tried on a laptop -- the desktop preview requirement in CLAUDE.md.
 */
export class MeasureTool {
  readonly group = new THREE.Group();
  readonly #reticle = new THREE.Group();
  readonly #segments = new THREE.Group();

  readonly #hitSources = new Map<XRInputSource, XRHitTestSource | null>();
  readonly #lastHit = new Map<XRInputSource, THREE.Vector3>();

  #active = false;
  #anchor: THREE.Vector3 | undefined;
  #live: Segment | undefined;
  #onMeasured: ((metres: number) => void) | undefined;

  constructor(
    readonly stage: Stage,
    readonly room = new DemoRoom(),
  ) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.018, 0.026, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.renderOrder = 12;
    const dot = new THREE.Mesh(
      new THREE.CircleGeometry(0.004, 16),
      new THREE.MeshBasicMaterial({ color: 0x1e6fd9, depthTest: false }),
    );
    dot.rotation.x = -Math.PI / 2;
    dot.renderOrder = 13;
    this.#reticle.add(ring, dot);
    this.#reticle.visible = false;

    this.group.add(this.#reticle, this.#segments, room.group);
    stage.scene.add(this.group);

    stage.renderer.xr.addEventListener('sessionstart', () => {
      this.room.group.visible = false;
      stage.scene.background = null;
    });
    stage.renderer.xr.addEventListener('sessionend', () => {
      this.room.group.visible = true;
      this.#hitSources.clear();
    });
  }

  onMeasured(handler: (metres: number) => void): void {
    this.#onMeasured = handler;
  }

  /**
   * Enables point placement and the reticle. It deliberately does NOT clear
   * what has been measured: the result panel says "Gemeten: 1,14 m", and the
   * line it refers to has to still be on the table while the student reads it.
   * Clearing happens on retry, or when the step changes.
   */
  setActive(active: boolean): void {
    this.#active = active;
    if (!active) this.#reticle.visible = false;
  }

  /**
   * Takes the tool out of the scene.
   *
   * The stage outlives a lesson, so a tool that only freed its textures would
   * leave its reticle and its finished measurements hanging in the next one.
   */
  dispose(): void {
    this.reset();
    for (const source of this.#hitSources.values()) source?.cancel();
    this.#hitSources.clear();
    this.group.removeFromParent();
  }

  reset(): void {
    this.#anchor = undefined;
    if (this.#live) {
      this.#segments.remove(this.#live.group);
      this.#live.dispose();
      this.#live = undefined;
    }
    this.#segments.clear();
  }

  /** A trigger pull or pinch: drop the first point, then the second. */
  select(ray: THREE.Ray, inputSource?: XRInputSource): void {
    if (!this.#active) return;
    const point = this.#pointFor(ray, inputSource);
    if (!point) return;

    if (!this.#anchor) {
      this.#anchor = point.clone();
      this.#live = new Segment(this.#anchor, point);
      this.#segments.add(this.#live.group);
      return;
    }

    this.#live?.moveEnd(point);
    const metres = this.#anchor.distanceTo(point);
    this.#anchor = undefined;
    this.#live = undefined;
    this.#onMeasured?.(metres);
  }

  update(frame?: XRFrame): void {
    if (this.stage.presenting && frame) this.#updateXrHits(frame);
    else this.#updateDesktopHit();

    if (this.#segments.children.length === 0) return;
    this.stage.activeCamera.getWorldPosition(CAMERA_SCRATCH);
    for (const child of this.#segments.children) {
      (child.userData['segment'] as Segment | undefined)?.faceCamera(CAMERA_SCRATCH);
    }
  }

  #pointFor(ray: THREE.Ray, inputSource?: XRInputSource): THREE.Vector3 | undefined {
    if (this.stage.presenting) {
      // The hit under the hand that fired, not just any tracked hand.
      //
      // Taking the first entry of the hit map measured with whichever source
      // happened to be inserted first -- usually the resting left hand, whose
      // ray barely moves between two taps. Every measurement on a real Quest
      // came back as 0.000 m because of it.
      const own = inputSource ? this.#lastHit.get(inputSource) : undefined;
      if (own) return own.clone();

      // No hit for that source (a pinch the hit-test missed): fall back to
      // where the reticle is drawn, which is what the student was aiming at.
      if (this.#reticle.visible) {
        return new THREE.Vector3().setFromMatrixPosition(this.#reticle.matrix);
      }
      return undefined;
    }
    const raycaster = new THREE.Raycaster();
    raycaster.set(ray.origin, ray.direction);
    return raycaster.intersectObjects(this.room.surfaces, false)[0]?.point.clone();
  }

  #updateXrHits(frame: XRFrame): void {
    const session = this.stage.renderer.xr.getSession();
    const reference = this.stage.renderer.xr.getReferenceSpace();
    if (!session || !reference) return;

    let shown = false;
    for (const controller of this.stage.controllers) {
      const source = controller.userData['source'] as XRInputSource | undefined;
      if (!source) continue;

      if (!this.#hitSources.has(source)) {
        this.#hitSources.set(source, null);
        void session
          .requestHitTestSource?.({ space: source.targetRaySpace })
          ?.then((hitSource) => this.#hitSources.set(source, hitSource))
          .catch(() => this.#hitSources.delete(source));
      }

      const hitSource = this.#hitSources.get(source);
      if (!hitSource) continue;

      const hits = frame.getHitTestResults(hitSource);
      const pose = hits[0]?.getPose(reference);
      if (!pose) {
        this.#lastHit.delete(source);
        continue;
      }

      const matrix = new THREE.Matrix4().fromArray(pose.transform.matrix);
      this.#lastHit.set(source, new THREE.Vector3().setFromMatrixPosition(matrix));
      if (!shown || source.handedness === 'right') {
        this.#reticle.matrix.copy(matrix);
        this.#reticle.matrixAutoUpdate = false;
        shown = true;
      }
    }

    this.#reticle.visible = shown && this.#active;
    if (this.#live && shown) {
      // Scratch vector: this runs every frame, and a fresh Vector3 per frame
      // is avoidable garbage on a mobile GPU.
      this.#live.moveEnd(SCRATCH.setFromMatrixPosition(this.#reticle.matrix));
    }
  }

  #updateDesktopHit(): void {
    this.#reticle.visible = false;
  }
}

/** Reused by the per-frame hit update; never retained. */
const SCRATCH = new THREE.Vector3();
const CAMERA_SCRATCH = new THREE.Vector3();

/** A measured line with its length floating at the midpoint. */
class Segment {
  readonly group = new THREE.Group();
  readonly #line: THREE.Line;
  readonly #label: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #canvas = document.createElement('canvas');
  readonly #texture: THREE.CanvasTexture;
  readonly #start: THREE.Vector3;
  readonly #endDot: THREE.Mesh;
  #end: THREE.Vector3;
  /** Last text drawn, so an unchanged label costs no texture upload. */
  #drawn = '';

  constructor(start: THREE.Vector3, end: THREE.Vector3) {
    this.#start = start.clone();
    this.#end = end.clone();

    const material = new THREE.LineBasicMaterial({ color: 0x1e6fd9, depthTest: false });
    this.#line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([this.#start, this.#end]),
      material,
    );
    this.#line.renderOrder = 14;

    this.#canvas.width = 512;
    this.#canvas.height = 160;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;
    this.#label = new THREE.Mesh(
      new THREE.PlaneGeometry(0.24, 0.075),
      new THREE.MeshBasicMaterial({ map: this.#texture, transparent: true, depthTest: false }),
    );
    this.#label.renderOrder = 15;

    const dot = (at: THREE.Vector3): THREE.Mesh => {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(0.008, 16, 12),
        new THREE.MeshBasicMaterial({ color: 0x1e6fd9, depthTest: false }),
      );
      mesh.position.copy(at);
      mesh.renderOrder = 14;
      return mesh;
    };

    this.#endDot = dot(this.#end);
    this.group.add(this.#line, this.#label, dot(this.#start), this.#endDot);
    this.group.userData['segment'] = this;
    this.moveEnd(end);
  }

  moveEnd(end: THREE.Vector3): void {
    this.#end = end.clone();
    // The end marker used to be placed once in the constructor, when start and
    // end were still the same point, and never moved again.
    this.#endDot.position.copy(this.#end);
    this.#line.geometry.setFromPoints([this.#start, this.#end]);
    this.#label.position
      .copy(this.#start)
      .lerp(this.#end, 0.5)
      .add(new THREE.Vector3(0, 0.04, 0));
    this.#drawLabel(this.#start.distanceTo(this.#end));
  }

  faceCamera(camera: THREE.Vector3): void {
    this.#label.lookAt(camera);
  }

  #drawLabel(metres: number): void {
    const text =
      metres < 1 ? `${Math.round(metres * 100)} cm` : `${metres.toFixed(2).replace('.', ',')} m`;

    // While a measurement is live this runs every frame. Redrawing the canvas
    // and re-uploading a 512x160 texture at 72-90 Hz for a number that changes
    // a couple of times a second is what made measuring feel jerky on a Quest.
    if (text === this.#drawn) return;
    this.#drawn = text;

    const ctx = this.#canvas.getContext('2d')!;
    ctx.clearRect(0, 0, 512, 160);
    ctx.fillStyle = '#1e6fd9';
    ctx.beginPath();
    ctx.roundRect(4, 4, 504, 152, 76);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 84px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 256, 84);
    this.#texture.needsUpdate = true;
  }

  dispose(): void {
    this.#texture.dispose();
    this.#line.geometry.dispose();
    this.#label.geometry.dispose();
    this.#label.material.dispose();
  }
}
